import { validateAndRefreshToken } from '../utils/tokenUtils';
import { config } from '../config';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface SpeechRateSettings {
  enabled: boolean;
  time_window: number;
  max_words: number;
}

class ApiService {
  private baseURL: string;
  
  constructor() {
    this.baseURL = config.server.backendUrl;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { token } = await validateAndRefreshToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    let data: any;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    if (!response.ok) {
      const error = {
        success: false,
        error: data.error || 'API_ERROR',
        message: data.message || `HTTP ${response.status}: ${response.statusText}`
      };
      throw error;
    }
    
    return data;
  }

  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API GET request failed:', error);
      throw error;
    }
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API PUT request failed:', error);
      throw error;
    }
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API POST request failed:', error);
      throw error;
    }
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API PATCH request failed:', error);
      throw error;
    }
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });

      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API DELETE request failed:', error);
      throw error;
    }
  }

  // Multipart form-data POST/PATCH — browser sets the Content-Type + boundary automatically
  async postFormData<T = any>(endpoint: string, formData: FormData, method: 'POST' | 'PATCH' = 'PATCH'): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      const { token } = await validateAndRefreshToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response);
    } catch (error) {
      console.error('API form-data request failed:', error);
      throw error;
    }
  }

  async submitEntryFeedback(sessionId: string, payload: {
    entryType: string;
    entryTimestamp: string;
    category?: string;
    rating: 'up' | 'down';
    reason?: string;
  }): Promise<ApiResponse> {
    return this.patch(`/conversations/sessions/${sessionId}/entry-feedback`, payload);
  }

  async submitSessionFeedback(sessionId: string, formData: FormData): Promise<ApiResponse> {
    return this.postFormData(`/conversations/sessions/${sessionId}/general-feedback`, formData);
  }
}

export const apiService = new ApiService();
export const apiClient = apiService; // Alias for compatibility

export const speechRateApi = {
  getSettings: () => apiService.get<SpeechRateSettings>('/speech-rate'),
  // Update settings is admin-only, not available in frontend
};

// Sales Playbook API
export interface SalesPlaybookRoleInfo {
  name: string;
  displayName: string;
}

export interface SalesPlaybookIndustryInfo {
  industry: string;
  displayName: string;
  roles: SalesPlaybookRoleInfo[];
}

export interface SalesPlaybookQuestion {
  _id: string;
  questionText: string;
  tags: string[];
  sortOrder: number;
  isActive: boolean;
}

export interface RoleData {
  industry: string;
  industryDisplayName: string;
  roleId: string;
  roleName: string;
  roleDisplayName: string;
  pitch: string[];
  advanced: string[];
  questions: SalesPlaybookQuestion[];
}

export const salesPlaybookApi = {
  getIndustries: async (): Promise<SalesPlaybookIndustryInfo[]> => {
    const response = await apiService.get<SalesPlaybookIndustryInfo[]>('/salesplaybooks/industries');
    if (response?.success && response?.data) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response as unknown as SalesPlaybookIndustryInfo[];
    }
    return [];
  },

  getRoleData: async (industry: string, roleName: string): Promise<RoleData | null> => {
    const response = await apiService.get<RoleData>(`/salesplaybooks/${industry}/roles/${roleName}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as unknown as RoleData;
  }
};

// Backward compatibility aliases
export type TalkTrackRoleInfo = SalesPlaybookRoleInfo;
export type TalkTrackIndustryInfo = SalesPlaybookIndustryInfo;
export type TalkTrackQuestion = SalesPlaybookQuestion;
export const talkTracksApi = salesPlaybookApi;